import React from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { TafChangeEvent } from '@taf-ui/core';
import AppStorage from 'Storage/Storage';
import DataForm from 'interfaces/PersonalDataForm';

import './InputAddress.css';
import InputCheckbox from 'FormInputs/InputCheckbox/InputCheckbox';
import InputZipCodeHouseNumber from 'FormInputs/InputAddress/InputZipCodeHouseNumber/InputZipCodeHouseNumber';
import InputNationality from 'FormInputs/InputNationality/InputNationality';
import Labels from '../../enums/Labels';
import InputText from '../InputText/InputText';

export type AddressModel = {
  zipCode: Extract<keyof DataForm, 'zipCode'>;
  zipCodeInt: Extract<keyof DataForm, 'zipCodeInt'>;
  houseNumber: Extract<keyof DataForm, 'number'>;
  houseLetter: Extract<keyof DataForm, 'suffix'>;
  street: Extract<keyof DataForm, 'street'>;
  city: Extract<keyof DataForm, 'city'>;
};

const defaultProps = {
  internationalAddressIsAllowed: false,
};
type DefaultProps = Readonly<typeof defaultProps>;

type Props = {
  internationalAddressIsAllowed: boolean;
  initialInternationalAddressState?: boolean;
  addressModel: AddressModel;
  mutator: UpdatedAddressMutator;
  mutatorForCountry?: UpdatedCountryMutator;
  name: 'insured';
} & Partial<DefaultProps>;

type State = {
  isInternationalAddress: boolean;
};

export type UpdatedAddress = {
  street: string;
  city: string;
};

export type UpdatedCountry = {
  country: string;
};

export type UpdatedAddressMutator = (updatedAddress: UpdatedAddress) => void;
export type UpdatedCountryMutator = (updatedCountry: UpdatedCountry) => void;

class InputAddress extends React.PureComponent<Props, State> {
  private readonly storage: AppStorage;

  static defaultProps: DefaultProps = defaultProps;

  constructor(props: Props) {
    super(props);

    this.storage = AppStorage.getInstance();

    this.state = {
      isInternationalAddress: props.initialInternationalAddressState || false,
    };
  }

  changeHandler = (e: CustomEvent<TafChangeEvent<boolean | string | number>>): void => {
    const { mutatorForCountry } = this.props;
    this.setState({
      isInternationalAddress: e.detail.value as boolean,
    });
    if (e.detail.value && mutatorForCountry) {
      mutatorForCountry({
        country: '',
      });
    }
  };

  render(): React.ReactElement {
    const {
      addressModel,
      internationalAddressIsAllowed,
      mutator,
      name,
    } = this.props;

    const {
      isInternationalAddress,
    } = this.state;

    return (
      <div className="address-wrapper">
        {internationalAddressIsAllowed ? (
          <Field type="checkbox" name="isInternationalAddress">
            {({ input, meta }: FieldRenderProps<string>): React.ReactElement => (
              <div className="international-address-check">
                <InputCheckbox
                  label={Labels.PERSONAL_DETAILS_INTERNATIONAL_ADDRESS}
                  input={input}
                  meta={meta}
                  onChangeHandler={this.changeHandler}
                />
              </div>
            )}
          </Field>
        ) : null}

        <InputZipCodeHouseNumber
          name={name}
          isInternationalAddress={isInternationalAddress}
          addressModel={addressModel}
          mutator={mutator}
        />

        <div className="street-and-residence-input-wrapper">
          {isInternationalAddress ? (
              <>
                <div className="street-input-wrapper international-field-wrapper">
                  <Field name={addressModel.street}>
                    {({ input, meta }: FieldRenderProps<string>): React.ReactElement => (
                      <InputText
                        labelWrapperClass="form-input"
                        label={Labels.PERSONAL_DETAILS_STREET}
                        placeholder={Labels.PERSONAL_DETAILS_STREET}
                        input={input}
                        meta={meta}
                        isRequired={true}
                      />
                    )}
                  </Field>
                </div>
                <div className="residence-input-wrapper international-field-wrapper">
                  <Field name={addressModel.city}>
                    {({ input, meta }: FieldRenderProps<string>): React.ReactElement => (
                      <InputText
                        labelWrapperClass="form-input"
                        label={Labels.PERSONAL_DETAILS_RESIDENCE}
                        placeholder={Labels.PERSONAL_DETAILS_RESIDENCE}
                        input={input}
                        meta={meta}
                        isRequired={true}
                      />
                    )}
                  </Field>
                </div>
                <div className="country-input-wrapper international-field-wrapper">
                  <InputNationality
                    fieldName="country"
                    placeholder={Labels.PERSONAL_DETAILS_COUNTRY}
                    label={Labels.PERSONAL_DETAILS_COUNTRY}
                    isRequired={isInternationalAddress}
                  />
                </div>
              </>
          ) : null}
        </div>
      </div>
    );
  }
}

export default InputAddress;
